<script>
  import { projectLoan, annualRepayment, projectStudyPhase } from "$calc/plans.js";
  import taxYear from "$data/2025-26.json";
  import BalanceChart from "./BalanceChart.svelte";
  import SegmentBar from "./SegmentBar.svelte";
  import { graduate } from "./appState.svelte.js";

  const PLANS = [
    { key: "plan1", label: "Plan 1", subtitle: "Pre-Sep 2012 undergrad · Northern Ireland" },
    { key: "plan2", label: "Plan 2", subtitle: "Sep 2012 – Jul 2023 undergrad (E&W)" },
    { key: "plan3", label: "Plan 3", subtitle: "Postgraduate Master's / Doctoral" },
    { key: "plan4", label: "Plan 4", subtitle: "Scotland (SAAS)" },
    { key: "plan5", label: "Plan 5", subtitle: "Aug 2023+ new undergrad (England)" },
  ];

  const currentYear = new Date().getFullYear();

  // Journey stage — drives everything below.
  let stage = $state("graduated"); // "planning" | "at_uni" | "graduated"

  // Common inputs
  let yearsRepaid = $state(0);

  // Planning / at-uni: uni-specific inputs
  let uniStartYear = $state(currentYear);
  let degreeYears = $state(3);
  let tuitionPerYear = $state(9535);
  let maintenancePerYear = $state(10227);
  let topUpPerYear = $state(0);
  let tuitionInflation = $state(2.5);

  // At-uni: how many years remaining
  let studyYearsRemainingInput = $state(2);
  let yearlyBorrowingAtUni = $state(19000);

  // Overpayment (applies to any repayment years)
  let overpayFixed = $state(0);
  let overpayPct = $state(0);

  // Axis toggle
  let axisMode = $state("age");

  // ------------- Derived -------------
  const planData = $derived(taxYear.studentLoans[graduate.planKey]);
  const yearsToUniStart = $derived(Math.max(0, uniStartYear - currentYear));
  const yearsSinceStart = $derived(Math.max(0, currentYear - uniStartYear));

  const ageAtUniStart = $derived(
    graduate.currentAge + (uniStartYear - currentYear),
  );
  const graduationYear = $derived(uniStartYear + degreeYears);
  const ageAtGraduation = $derived(ageAtUniStart + degreeYears);

  // Planning: inflate tuition to uni-start values
  const inflatedTuition = $derived(
    tuitionPerYear * Math.pow(1 + tuitionInflation / 100, yearsToUniStart),
  );
  const inflatedMaintenance = $derived(
    maintenancePerYear * Math.pow(1 + tuitionInflation / 100, yearsToUniStart),
  );
  const inflatedTopUp = $derived(
    topUpPerYear * Math.pow(1 + tuitionInflation / 100, yearsToUniStart),
  );
  const yearlyBorrowingPlanning = $derived(
    inflatedTuition + inflatedMaintenance + inflatedTopUp,
  );

  // Compute study phase for planning + at-uni (used only to derive
  // balance-at-graduation for display)
  const study = $derived.by(() => {
    if (stage === "planning") {
      return projectStudyPhase({
        yearlyBorrowing: yearlyBorrowingPlanning,
        studyYears: degreeYears,
        rpiMarch: graduate.rpiMarchPct / 100,
      });
    }
    if (stage === "at_uni") {
      return projectStudyPhase({
        yearlyBorrowing: Math.max(0, yearlyBorrowingAtUni),
        studyYears: Math.max(0, studyYearsRemainingInput),
        rpiMarch: graduate.rpiMarchPct / 100,
      });
    }
    return null;
  });

  // Overpayment: fixed + % of salary
  const overpayFromPct = $derived(
    Math.max(0, graduate.salary) * (Math.max(0, overpayPct) / 100),
  );
  const effectiveOverpayment = $derived(
    Math.max(0, overpayFixed) + overpayFromPct,
  );

  // Pension match check
  const matchRoomPct = $derived(
    Math.max(0, graduate.pensionEmployerMatchCapPct - graduate.pensionEmployeePct),
  );
  const matchRoomValue = $derived(
    Math.max(0, graduate.salary) * (matchRoomPct / 100) * (1 + Math.max(0, graduate.pensionEmployerMatchRatio)),
  );
  const overpayingWhileMatchAvailable = $derived(
    effectiveOverpayment > 0 && matchRoomPct > 0 && graduate.pensionEmployerMatchRatio > 0,
  );

  // Unified projection: starting balance and study params vary by stage
  const projectionInputs = $derived.by(() => {
    if (stage === "planning") {
      return {
        startingBalance: 0,
        studyYearsRemaining: degreeYears,
        yearlyBorrowingRemaining: yearlyBorrowingPlanning,
        ageAtStart: planData.wipeAtAge != null ? ageAtUniStart : null,
      };
    }
    if (stage === "at_uni") {
      // Use the current balance the user has already borrowed as starting
      // balance; project remaining study on top.
      return {
        startingBalance: Math.max(0, graduate.loanBalance),
        studyYearsRemaining: Math.max(0, studyYearsRemainingInput),
        yearlyBorrowingRemaining: Math.max(0, yearlyBorrowingAtUni),
        ageAtStart: planData.wipeAtAge != null ? graduate.currentAge : null,
      };
    }
    return {
      startingBalance: Math.max(0, graduate.loanBalance),
      studyYearsRemaining: 0,
      yearlyBorrowingRemaining: 0,
      ageAtStart: planData.wipeAtAge != null ? graduate.currentAge : null,
    };
  });

  const result = $derived(
    projectLoan({
      ...projectionInputs,
      startingSalary: Math.max(0, graduate.salary),
      salaryGrowth: graduate.salaryGrowthPct / 100,
      rpiMarch: graduate.rpiMarchPct / 100,
      bankRate: graduate.bankRatePct / 100,
      planData,
      yearsRepaid,
      annualOverpayment: effectiveOverpayment,
    }),
  );

  const monthlyRepaymentNow = $derived(
    stage === "graduated"
      ? annualRepayment(Math.max(0, graduate.salary), planData) / 12
      : 0,
  );

  // For the balance-at-graduation card (only shown in planning/at-uni)
  const balanceAtGraduation = $derived(study?.balanceAtGraduation ?? 0);

  // Publish uniProjectedBalance so we can still show mismatches elsewhere
  $effect(() => {
    graduate.uniProjectedBalance = study
      ? Math.round(study.balanceAtGraduation)
      : null;
  });

  // ------------- Chart ticks (age/year axis) -------------
  const projectionOriginAge = $derived(
    stage === "planning" ? ageAtUniStart : graduate.currentAge,
  );
  const projectionOriginYear = $derived(
    stage === "planning" ? uniStartYear : currentYear,
  );

  const chartXTicks = $derived.by(() => {
    if (result.years.length === 0) return [];
    const nOffsets = result.years.length;
    const origin = axisMode === "age" ? projectionOriginAge : projectionOriginYear;
    const min = origin;
    const max = origin + nOffsets;
    const range = max - min;
    let step;
    if (range <= 8) step = 1;
    else if (range <= 20) step = 2;
    else if (range <= 60) step = 5;
    else step = 10;
    const startTick = Math.ceil(min / step) * step;
    const offsets = [];
    for (let v = startTick; v <= max; v += step) offsets.push(v - origin);
    return offsets;
  });

  function xTickLabelFn(offset) {
    return String(
      (axisMode === "age" ? projectionOriginAge : projectionOriginYear) + offset,
    );
  }

  // ------------- Formatters -------------
  const fmtGBP = (n) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
  const fmtPct = (n) =>
    new Intl.NumberFormat("en-GB", { style: "percent", maximumFractionDigits: 1 }).format(n);
</script>

<section class="screen">
  <div class="intro">
    <h2>Your loan</h2>
    <p>
      Model your UK student loan across the whole lifecycle. Tell us where
      you are in the journey and the numbers adapt.
    </p>
  </div>

  <!-- Journey stage picker -->
  <div class="stage-picker card">
    <div class="stage-label">Where are you?</div>
    <div class="stage-buttons" role="tablist">
      <button
        type="button"
        class:active={stage === "planning"}
        onclick={() => (stage = "planning")}
        role="tab"
      >
        Planning to go
      </button>
      <button
        type="button"
        class:active={stage === "at_uni"}
        onclick={() => (stage = "at_uni")}
        role="tab"
      >
        At uni now
      </button>
      <button
        type="button"
        class:active={stage === "graduated"}
        onclick={() => (stage = "graduated")}
        role="tab"
      >
        Already graduated
      </button>
    </div>
  </div>

  <div class="layout">
    <aside class="inputs card">
      <label class="field">
        <span class="label">Plan</span>
        <select bind:value={graduate.planKey}>
          {#each PLANS as p (p.key)}
            <option value={p.key}>{p.label}</option>
          {/each}
        </select>
      </label>
      <p class="hint">
        {PLANS.find(p => p.key === graduate.planKey).subtitle}
      </p>

      <label class="field">
        <span class="label">Current age</span>
        <input type="number" min="14" max="80" step="1" bind:value={graduate.currentAge} />
        <span class="hint-sync">Shared across tabs</span>
      </label>

      {#if stage === "planning"}
        <h3 class="section-heading">Uni details</h3>
        <label class="field">
          <span class="label">Year uni starts</span>
          <input
            type="number"
            min={currentYear}
            max={currentYear + 15}
            step="1"
            bind:value={uniStartYear}
          />
          <span class="hint">
            {yearsToUniStart === 0 ? "This year" : `${yearsToUniStart} yr${yearsToUniStart === 1 ? "" : "s"} away`}
            · age {ageAtUniStart} at start · graduate {graduationYear} at {ageAtGraduation}
          </span>
        </label>
        <label class="field">
          <span class="label">Degree length</span>
          <div class="suffix-input">
            <input type="number" min="1" max="7" step="1" bind:value={degreeYears} />
            <span class="suffix">yrs</span>
          </div>
        </label>

        <h3 class="section-heading">Costs (today's money)</h3>
        <p class="hint mb-tight">Inflated to your start year using the % below.</p>
        <label class="field">
          <span class="label">Tuition per year</span>
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input type="number" min="0" step="100" bind:value={tuitionPerYear} />
          </div>
        </label>
        <label class="field">
          <span class="label">Maintenance loan per year</span>
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input type="number" min="0" step="100" bind:value={maintenancePerYear} />
          </div>
        </label>
        <label class="field">
          <span class="label">Other top-up per year</span>
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input type="number" min="0" step="100" bind:value={topUpPerYear} />
          </div>
        </label>
        {#if yearsToUniStart > 0}
          <label class="field">
            <span class="label">Tuition inflation</span>
            <div class="suffix-input">
              <input type="number" min="0" max="15" step="0.1" bind:value={tuitionInflation} />
              <span class="suffix">%</span>
            </div>
          </label>
        {/if}
      {:else if stage === "at_uni"}
        <h3 class="section-heading">Uni details</h3>
        <label class="field">
          <span class="label">Year uni started</span>
          <input
            type="number"
            min={currentYear - 10}
            max={currentYear}
            step="1"
            bind:value={uniStartYear}
          />
          <span class="hint">
            {yearsSinceStart === 0 ? "This year" : `${yearsSinceStart} yr${yearsSinceStart === 1 ? "" : "s"} in`}
            · started at age {ageAtUniStart}
          </span>
        </label>
        <label class="field">
          <span class="label">Degree length</span>
          <div class="suffix-input">
            <input type="number" min="1" max="7" step="1" bind:value={degreeYears} />
            <span class="suffix">yrs</span>
          </div>
        </label>
        <label class="field">
          <span class="label">Balance so far</span>
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input type="number" min="0" step="500" bind:value={graduate.loanBalance} />
          </div>
          <span class="hint-sync">Shared across tabs</span>
        </label>
        <label class="field">
          <span class="label">Years remaining</span>
          <div class="suffix-input">
            <input type="number" min="0" max="7" step="1" bind:value={studyYearsRemainingInput} />
            <span class="suffix">yrs</span>
          </div>
        </label>
        <label class="field">
          <span class="label">Annual borrowing during remaining years</span>
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input type="number" min="0" step="500" bind:value={yearlyBorrowingAtUni} />
          </div>
        </label>
      {:else}
        <h3 class="section-heading">Your loan today</h3>
        <label class="field">
          <span class="label">Current balance</span>
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input type="number" min="0" step="500" bind:value={graduate.loanBalance} />
          </div>
          <span class="hint-sync">Shared with Pension &amp; Gift</span>
        </label>
        <label class="field">
          <span class="label">Years already repaid</span>
          <input type="number" min="0" max="40" step="1" bind:value={yearsRepaid} />
          <span class="hint">Subtracts from the {planData.wipeAfterYears}-year wipe clock</span>
        </label>
      {/if}

      <h3 class="section-heading">
        {stage === "graduated" ? "Salary" : "After graduation"}
      </h3>
      <label class="field">
        <span class="label">
          {stage === "graduated" ? "Current salary" : "Expected starting salary"}
        </span>
        <div class="prefix-input">
          <span class="prefix">£</span>
          <input type="number" min="0" step="1000" bind:value={graduate.salary} />
        </div>
        <span class="hint-sync">Shared with Take-home &amp; Pension</span>
      </label>
      <label class="field">
        <span class="label">Salary growth (per year)</span>
        <div class="suffix-input">
          <input type="number" min="-5" max="15" step="0.1" bind:value={graduate.salaryGrowthPct} />
          <span class="suffix">%</span>
        </div>
        <span class="hint-sync">Shared across tabs</span>
      </label>
      <label class="field">
        <span class="label">RPI (March)</span>
        <div class="suffix-input">
          <input type="number" min="0" max="20" step="0.01" bind:value={graduate.rpiMarchPct} />
          <span class="suffix">%</span>
        </div>
        <span class="hint-sync">Shared across tabs</span>
      </label>

      <div class="overpay-block">
        <div class="section-label">Voluntary overpayment</div>
        <p class="hint mb-tight">
          Extra you'd pay on top of the mandatory 9% (6% Plan 3).
        </p>
        <label class="field">
          <span class="label">Fixed lump sum</span>
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input type="number" min="0" step="100" bind:value={overpayFixed} />
          </div>
          <span class="hint">Per year</span>
        </label>
        <label class="field">
          <span class="label">% of salary</span>
          <div class="suffix-input">
            <input type="number" min="0" max="100" step="0.5" bind:value={overpayPct} />
            <span class="suffix">%</span>
          </div>
          <span class="hint">
            {#if overpayPct > 0}
              = {fmtGBP(overpayFromPct)}/yr at current salary
            {:else}
              Scales with salary as you earn more
            {/if}
          </span>
        </label>
      </div>
    </aside>

    <section class="output">
      {#if overpayingWhileMatchAvailable}
        <div class="warning-card card">
          <div class="warning-header">
            <span class="warning-icon">⚠</span>
            <div>
              <div class="warning-title">Overpaying loan before filling employer match</div>
              <div class="warning-body">
                You're overpaying by <strong>{fmtGBP(effectiveOverpayment)}/yr</strong>
                but have <strong>{matchRoomPct}%</strong> of salary of unclaimed
                employer pension match — roughly <strong>{fmtGBP(matchRoomValue)}/yr</strong>
                of employer money on top. Instant 100%+ return the loan overpayment
                can't match. See the Pension tab.
              </div>
            </div>
          </div>
        </div>
      {/if}

      {#if study != null && stage !== "graduated"}
        <div class="phase-card card phase-study">
          <div class="phase-label">
            {stage === "planning" ? "Balance the day you graduate" : "Balance the day you graduate"}
          </div>
          <div class="phase-value">
            {fmtGBP(
              stage === "at_uni"
                ? graduate.loanBalance + (study?.balanceAtGraduation ?? 0) - 0
                : balanceAtGraduation
            )}
          </div>
          <ul class="phase-detail">
            <li>Borrowed: <strong>{fmtGBP(study.totalBorrowed + (stage === "at_uni" ? graduate.loanBalance : 0))}</strong></li>
            <li>Interest during study: <strong>{fmtGBP(study.totalInterestAccruedInStudy)}</strong></li>
          </ul>
        </div>
      {/if}

      <div class="headline card {result.outcome}">
        {#if result.outcome === "cleared"}
          <div class="outcome-label">Cleared</div>
          <div class="outcome-value">
            in <strong>{result.yearsToOutcome} years</strong>
          </div>
          <div class="outcome-detail">
            Total repaid: <strong>{fmtGBP(result.totalRepaid)}</strong>
          </div>
          {#if result.totalRepaid > 0}
            <div class="repay-split-new">
              <SegmentBar
                variant="dark"
                trackColor="rgba(255, 255, 255, 0.15)"
                segments={[
                  { label: "Capital", value: result.capitalRepaid, color: "rgba(255, 255, 255, 0.85)", hint: "Principal paid down" },
                  { label: "Interest", value: result.interestRepaid, color: "rgba(255, 255, 255, 0.35)", hint: "Interest serviced" },
                ]}
              />
            </div>
          {/if}
        {:else}
          <div class="outcome-label">Written off</div>
          <div class="outcome-value">
            after <strong>{result.yearsToOutcome} years</strong>
          </div>
          <div class="outcome-detail">
            Total repaid before wipe: <strong>{fmtGBP(result.totalRepaid)}</strong>
            {#if result.capitalRepaid < 1}
              — every pound serviced interest. Overpayment would be a gift to Treasury.
            {/if}
          </div>
          {#if result.totalRepaid > 0}
            <div class="repay-split-new">
              <SegmentBar
                variant="dark"
                trackColor="rgba(255, 255, 255, 0.15)"
                segments={[
                  { label: "Capital", value: result.capitalRepaid, color: "rgba(255, 255, 255, 0.85)", hint: "Principal that got paid down" },
                  { label: "Interest", value: result.interestRepaid, color: "rgba(255, 255, 255, 0.35)", hint: "Interest serviced" },
                ]}
              />
            </div>
          {/if}
        {/if}
      </div>

      {#if stage === "graduated"}
        <div class="quick-stats">
          <div class="stat">
            <span class="stat-label">Monthly repayment now</span>
            <span class="stat-value">{fmtGBP(monthlyRepaymentNow)}</span>
          </div>
          <div class="stat">
            <span class="stat-label">Threshold</span>
            <span class="stat-value">{fmtGBP(planData.repaymentThreshold)}</span>
          </div>
          <div class="stat">
            <span class="stat-label">Rate</span>
            <span class="stat-value">{fmtPct(planData.repaymentRate)}</span>
          </div>
        </div>
      {/if}

      <div class="chart-card card">
        <div class="chart-head">
          <h3>Balance over time</h3>
          <div class="axis-toggle" role="group">
            <button
              type="button"
              class:active={axisMode === "age"}
              onclick={() => (axisMode = "age")}
            >Age</button>
            <button
              type="button"
              class:active={axisMode === "year"}
              onclick={() => (axisMode = "year")}
            >Year</button>
          </div>
        </div>
        <BalanceChart
          years={result.years}
          outcome={result.outcome}
          phased
          xTicks={chartXTicks}
          xTickLabel={xTickLabelFn}
        />
        <div class="legend">
          {#if projectionInputs.studyYearsRemaining > 0}
            <span class="legend-item">
              <span class="legend-swatch swatch-study"></span> At uni
            </span>
          {/if}
          <span class="legend-item">
            <span class="legend-swatch swatch-gap"></span>
            Pre-threshold (interest accrues, no repayments)
          </span>
          <span class="legend-item">
            <span class="legend-swatch swatch-repay-{result.outcome}"></span>
            Repayment ({result.outcome === "cleared" ? "clears" : "wipes"})
          </span>
        </div>
      </div>

      {#if result.totalVoluntaryRepaid > 0}
        <div class="overpay-card card">
          <div class="overpay-head">
            <div class="overpay-label">Voluntary overpayment impact</div>
            <div class="overpay-figure">{fmtGBP(result.totalVoluntaryRepaid)}</div>
          </div>
          <div class="overpay-detail">
            paid on top of mandatory <strong>{fmtGBP(result.totalMandatoryRepaid)}</strong>
            over the loan life.
            {#if result.outcome === "wiped"}
              Note: loan still wipes — overpayment gave no benefit. Consider the pension instead.
            {/if}
          </div>
        </div>
      {/if}

      <details class="rows card">
        <summary>Year-by-year detail</summary>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Yr</th>
                <th>Phase</th>
                <th>Salary</th>
                <th>Interest</th>
                <th>Mandatory</th>
                {#if result.totalVoluntaryRepaid > 0}<th>Voluntary</th>{/if}
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {#each result.years as row (row.year)}
                <tr class="row-phase-{row.phase}">
                  <td>{row.year}</td>
                  <td class="phase-cell">
                    {row.phase === "study" ? "Uni" : row.phase === "gap" ? "Pre-pay" : "Repay"}
                  </td>
                  <td>{row.phase === "repay" ? fmtGBP(row.salary) : "—"}</td>
                  <td>{fmtGBP(row.interestAdded)}</td>
                  <td>{row.mandatoryRepayment > 0 ? fmtGBP(row.mandatoryRepayment) : "—"}</td>
                  {#if result.totalVoluntaryRepaid > 0}
                    <td>{row.voluntaryRepayment > 0 ? fmtGBP(row.voluntaryRepayment) : "—"}</td>
                  {/if}
                  <td>{fmtGBP(row.closingBalance)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  </div>
</section>

<style>
  .screen { display: flex; flex-direction: column; gap: 1.25rem; }
  .intro h2 { margin-bottom: 0.35rem; }
  .intro p { color: var(--fg-muted); margin: 0; font-size: 0.95rem; max-width: 62ch; }

  .stage-picker { padding: 1.15rem 1.25rem; }
  .stage-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: var(--fg-muted);
    margin-bottom: 0.65rem;
  }
  .stage-buttons { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .stage-buttons button {
    background: var(--bg);
    border: 1.5px solid var(--card-border);
    color: var(--fg);
    padding: 0.55rem 1rem;
    border-radius: 999px;
    font-weight: 600;
    font-size: 0.9rem;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    flex: 1 1 auto;
  }
  .stage-buttons button:hover { border-color: var(--accent); }
  .stage-buttons button.active {
    background: var(--accent);
    color: var(--accent-fg);
    border-color: var(--accent);
  }

  .layout { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
  @media (min-width: 900px) {
    .layout { grid-template-columns: minmax(300px, 340px) 1fr; }
  }

  .card {
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.9rem;
    padding: 1.15rem 1.25rem;
    box-shadow: var(--card-shadow);
  }

  .section-heading {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--card-border);
    margin-bottom: 0.9rem;
    font-size: 0.95rem;
  }
  .section-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: var(--fg-muted);
    margin-bottom: 0.5rem;
  }

  .field { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.85rem; }
  .label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--fg-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  input, select {
    font: inherit;
    font-size: 1rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--card-border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    width: 100%;
  }
  input:focus, select:focus { outline: none; border-color: var(--accent); background: var(--card); }
  .prefix-input, .suffix-input { position: relative; }
  .prefix-input input { padding-left: 1.75rem; }
  .prefix { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--fg-muted); font-weight: 600; pointer-events: none; }
  .suffix-input input { padding-right: 2.25rem; }
  .suffix { position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--fg-muted); font-weight: 600; pointer-events: none; font-size: 0.85rem; }
  .hint { color: var(--fg-subtle); font-size: 0.78rem; }
  .hint-sync { color: var(--accent); font-size: 0.7rem; font-weight: 500; opacity: 0.75; font-style: italic; }
  .mb-tight { margin: -0.35rem 0 0.75rem; }

  .overpay-block { margin-top: 0.5rem; padding-top: 1rem; border-top: 1px solid var(--card-border); }

  .output { display: flex; flex-direction: column; gap: 1.25rem; }

  .warning-card {
    border: 2px solid var(--warning);
    background: rgba(207, 138, 44, 0.06);
  }
  .warning-header { display: flex; gap: 0.75rem; align-items: flex-start; }
  .warning-icon { font-size: 1.35rem; color: var(--warning); line-height: 1; margin-top: 0.1rem; }
  .warning-title { font-weight: 800; color: var(--fg); font-size: 1rem; margin-bottom: 0.3rem; }
  .warning-body { color: var(--fg); font-size: 0.92rem; line-height: 1.55; }
  .warning-body strong { color: var(--warning); }

  .phase-card {
    padding: 1.35rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .phase-study {
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%);
    color: var(--accent-fg);
    border: none;
  }
  .phase-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 700;
    opacity: 0.85;
  }
  .phase-value {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
  }
  .phase-detail {
    list-style: none;
    padding: 0;
    margin: 0.35rem 0 0;
    font-size: 0.88rem;
  }
  .phase-detail li {
    padding: 0.2rem 0;
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  }
  .phase-detail li:last-child { border-bottom: none; }

  .headline {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%);
    color: var(--accent-fg);
    border: none;
  }
  .headline.wiped {
    background: linear-gradient(135deg, var(--coral) 0%, #a34747 100%);
  }
  .outcome-label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    opacity: 0.8;
    font-weight: 700;
  }
  .outcome-value { font-size: 1.75rem; font-weight: 300; line-height: 1.2; }
  .outcome-value strong { font-weight: 700; }
  .outcome-detail { margin-top: 0.5rem; font-size: 0.95rem; opacity: 0.95; line-height: 1.45; }
  .outcome-detail strong { font-weight: 700; }
  .repay-split-new { margin-top: 1rem; padding-top: 1rem; border-top: 1px solid rgba(255, 255, 255, 0.18); }

  .quick-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }
  .stat {
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.7rem;
    padding: 0.85rem 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    box-shadow: var(--card-shadow);
  }
  .stat-label { font-size: 0.7rem; color: var(--fg-muted); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
  .stat-value { font-size: 1.15rem; font-weight: 700; color: var(--fg); }

  .chart-card h3 { margin: 0; }
  .chart-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-bottom: 0.9rem;
  }
  .axis-toggle {
    display: inline-flex;
    background: var(--bg-2);
    border-radius: 999px;
    padding: 3px;
  }
  .axis-toggle button {
    background: transparent;
    border: none;
    color: var(--fg-muted);
    padding: 0.3rem 0.85rem;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
    transition: background 0.15s, color 0.15s;
  }
  .axis-toggle button.active {
    background: var(--accent);
    color: var(--accent-fg);
  }

  .legend { display: flex; gap: 1rem; flex-wrap: wrap; margin-top: 0.75rem; font-size: 0.82rem; color: var(--fg-muted); }
  .legend-item { display: inline-flex; align-items: center; gap: 0.4rem; }
  .legend-swatch { display: inline-block; width: 14px; height: 3px; border-radius: 2px; }
  .swatch-study { background: var(--accent); }
  .swatch-gap { background: var(--warning); }
  .swatch-repay-cleared { background: var(--success); }
  .swatch-repay-wiped { background: var(--coral); }

  .overpay-card { border-color: var(--success); border-width: 2px; }
  .overpay-head { display: flex; align-items: baseline; gap: 0.75rem; margin-bottom: 0.35rem; }
  .overpay-label { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--success); font-weight: 700; }
  .overpay-figure { font-size: 1.4rem; font-weight: 800; color: var(--success); font-variant-numeric: tabular-nums; }
  .overpay-detail { font-size: 0.9rem; color: var(--fg); line-height: 1.5; }
  .overpay-detail strong { color: var(--fg); font-weight: 700; }

  .rows summary {
    cursor: pointer;
    font-weight: 700;
    color: var(--fg);
    padding: 0.35rem 0;
    list-style: none;
  }
  .rows summary::-webkit-details-marker { display: none; }
  .rows summary::before { content: "▸ "; color: var(--accent); }
  .rows[open] summary::before { content: "▾ "; }
  .table-scroll { overflow-x: auto; margin-top: 0.75rem; }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.87rem;
    font-variant-numeric: tabular-nums;
  }
  th, td {
    text-align: right;
    padding: 0.4rem 0.65rem;
    border-bottom: 1px solid var(--card-border);
  }
  th:first-child, td:first-child { text-align: left; }
  th:nth-child(2), td:nth-child(2) { text-align: center; }
  th {
    color: var(--fg-muted);
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    background: var(--bg-2);
  }
  .row-phase-study .phase-cell { color: var(--accent); font-weight: 700; }
  .row-phase-gap .phase-cell { color: var(--warning); font-weight: 700; }
  tbody tr:hover { background: var(--accent-tint); }

  @media (max-width: 500px) {
    .quick-stats { grid-template-columns: 1fr; }
    .outcome-value { font-size: 1.5rem; }
    .phase-value { font-size: 1.4rem; }
  }
</style>
